'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronDownGlyph } from '../../../internal/icons.js';
import { Checkbox } from '../../forms/checkbox/index.js';
/**
 * Data table primitives. Wrap in `<Card flush>` for a framed table. Pass a
 * `<TableCaption>` or `aria-label` so the table has an accessible name.
 */
export const Table = forwardRef(function Table({ density = 'comfortable', stickyHeader = false, stickyFirstColumn = false, maxHeight, className, ...rest }, ref) {
    return (_jsx("div", { className: "mn-table-wrap", style: maxHeight === undefined ? undefined : { maxHeight }, children: _jsx("table", { ref: ref, ...rest, "data-density": density, "data-sticky": stickyHeader || undefined, "data-sticky-first": stickyFirstColumn || undefined, className: cx('mn-table', className) }) }));
});
export function TableHeader({ className, ...rest }) {
    return _jsx("thead", { ...rest, className: cx('mn-table__header', className) });
}
export function TableBody({ className, ...rest }) {
    return _jsx("tbody", { ...rest, className: cx('mn-table__body', className) });
}
export function TableRow({ selected, interactive, className, ...rest }) {
    return (_jsx("tr", { ...rest, "aria-selected": selected ?? rest['aria-selected'], "data-selected": selected || undefined, "data-interactive": interactive || undefined, className: cx('mn-table__row', className) }));
}
export function TableHead({ sort, onSortChange, numeric, className, children, scope = 'col', ...rest }) {
    const sortable = sort !== undefined;
    return (_jsx("th", { ...rest, scope: scope, "aria-sort": sortable && sort !== 'none' ? sort : undefined, "data-numeric": numeric || undefined, className: cx('mn-table__head', className), children: sortable ? (_jsx(SortButton, { direction: sort, onClick: () => onSortChange?.(sort === 'ascending' ? 'descending' : 'ascending'), children: children })) : (children) }));
}
/** Column sort toggle; its accessible name states the current direction. */
export function SortButton({ direction, onClick, children }) {
    const state = direction === 'ascending'
        ? 'sorted ascending'
        : direction === 'descending'
            ? 'sorted descending'
            : 'not sorted';
    return (_jsxs("button", { type: "button", className: "mn-table__sort", "data-direction": direction, onClick: onClick, children: [children, _jsxs("span", { className: "mn-visually-hidden", children: [", ", state] }), _jsx(ChevronDownGlyph, { className: "mn-table__sort-icon" })] }));
}
export function TableCell({ numeric, className, ...rest }) {
    return (_jsx("td", { ...rest, "data-numeric": numeric || undefined, className: cx('mn-table__cell', className) }));
}
export function TableCaption({ className, ...rest }) {
    return _jsx("caption", { ...rest, className: cx('mn-table__caption', className) });
}
/** Narrow checkbox column for row selection. */
export function TableSelectCell({ label, header = false, className, ...rest }) {
    const Tag = header ? 'th' : 'td';
    return (_jsx(Tag, { ...(header ? { scope: 'col' } : {}), className: cx('mn-table__select', header ? 'mn-table__head' : 'mn-table__cell', className), children: _jsx(Checkbox, { "aria-label": label, ...rest }) }));
}
//# sourceMappingURL=data-table.js.map